
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UserInteraction {
  interactionType: 'chart_view' | 'filter_apply' | 'insight_view' | 'dashboard_generate' | 'kpi_view';
  chartType?: string;
  industry?: string;
  dataColumns?: string[];
  filtersApplied?: any;
  sessionDuration?: number;
  metadata?: any;
}

interface UserPreferences {
  preferredChartTypes: string[];
  preferredKpiFormats: string[];
  commonFilterPatterns: any[];
  dataColumnAffinities: Record<string, number>;
  insightCategories: string[];
  confidenceScore: number;
}

// Type guard functions to safely cast JSONB data
const isStringArray = (value: any): value is string[] => {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
};

const isRecord = (value: any): value is Record<string, number> => {
  return value && typeof value === 'object' && !Array.isArray(value);
};

const isArray = (value: any): value is any[] => {
  return Array.isArray(value);
};

export const useSelfLearningAnalytics = () => {
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(false);

  // Load user preferences on mount
  useEffect(() => {
    loadUserPreferences();
    setSessionStartTime(new Date());
  }, []);

  const loadUserPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_analytics_preferences')
        .select('*')
        .eq('user_id', user.id)
        .order('confidence_score', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading user preferences:', error);
        return;
      }

      if (data) {
        setUserPreferences({
          preferredChartTypes: isStringArray(data.preferred_chart_types) ? data.preferred_chart_types : [],
          preferredKpiFormats: isStringArray(data.preferred_kpi_formats) ? data.preferred_kpi_formats : [],
          commonFilterPatterns: isArray(data.common_filter_patterns) ? data.common_filter_patterns : [],
          dataColumnAffinities: isRecord(data.data_column_affinities) ? data.data_column_affinities : {},
          insightCategories: isStringArray(data.insight_categories) ? data.insight_categories : [],
          confidenceScore: typeof data.confidence_score === 'number' ? data.confidence_score : 0
        });
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  const trackInteraction = useCallback(async (interaction: UserInteraction) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const sessionDuration = interaction.sessionDuration || 
        Math.floor((new Date().getTime() - sessionStartTime.getTime()) / 1000);

      const { error } = await supabase
        .from('user_dashboard_interactions')
        .insert({
          user_id: user.id,
          interaction_type: interaction.interactionType,
          chart_type: interaction.chartType,
          industry: interaction.industry,
          data_columns: interaction.dataColumns,
          filters_applied: interaction.filtersApplied,
          session_duration: sessionDuration,
          interaction_metadata: interaction.metadata || {}
        });

      if (error) {
        console.error('Error tracking interaction:', error);
      } else {
        // Reload preferences after tracking to get updated learning
        setTimeout(() => loadUserPreferences(), 1000);
      }
    } catch (error) {
      console.error('Error tracking interaction:', error);
    }
  }, [sessionStartTime]);

  const getOptimizedChartRecommendations = useCallback((
    dataColumns: any[],
    industry: string
  ): { chartTypes: string[], kpiFormats: string[] } => {
    const defaultChartTypes = ['bar', 'line', 'pie', 'area'];
    const defaultKpiFormats = ['number', 'currency', 'percentage'];

    if (!userPreferences || userPreferences.confidenceScore < 0.3) {
      // Use default recommendations for new users or low confidence
      return {
        chartTypes: defaultChartTypes,
        kpiFormats: defaultKpiFormats
      };
    }

    // Apply learned preferences
    const recommendedChartTypes = userPreferences.preferredChartTypes.length > 0
      ? userPreferences.preferredChartTypes
      : defaultChartTypes;

    const recommendedKpiFormats = userPreferences.preferredKpiFormats.length > 0
      ? userPreferences.preferredKpiFormats
      : defaultKpiFormats;

    return {
      chartTypes: recommendedChartTypes,
      kpiFormats: recommendedKpiFormats
    };
  }, [userPreferences]);

  const getPrioritizedColumns = useCallback((columns: any[]): any[] => {
    if (!userPreferences || !userPreferences.dataColumnAffinities) {
      return columns;
    }

    // Sort columns based on user's historical preferences
    return columns.sort((a, b) => {
      const affinityA = userPreferences.dataColumnAffinities[a.name] || 0;
      const affinityB = userPreferences.dataColumnAffinities[b.name] || 0;
      return affinityB - affinityA;
    });
  }, [userPreferences]);

  const getSmartFilterSuggestions = useCallback((columns: any[]): any[] => {
    if (!userPreferences || !userPreferences.commonFilterPatterns.length) {
      return [];
    }

    // Return filters that user commonly applies
    return userPreferences.commonFilterPatterns.slice(0, 3);
  }, [userPreferences]);

  const updatePreferencesManually = async (preferences: Partial<UserPreferences>) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_analytics_preferences')
        .upsert({
          user_id: user.id,
          industry: 'general',
          preferred_chart_types: preferences.preferredChartTypes || [],
          preferred_kpi_formats: preferences.preferredKpiFormats || [],
          common_filter_patterns: preferences.commonFilterPatterns || [],
          data_column_affinities: preferences.dataColumnAffinities || {},
          insight_categories: preferences.insightCategories || [],
          confidence_score: Math.min((userPreferences?.confidenceScore || 0) + 0.2, 1.0)
        });

      if (error) {
        toast.error('Failed to update preferences');
        console.error('Error updating preferences:', error);
      } else {
        toast.success('Learning preferences updated');
        await loadUserPreferences();
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update preferences');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    userPreferences,
    trackInteraction,
    getOptimizedChartRecommendations,
    getPrioritizedColumns,
    getSmartFilterSuggestions,
    updatePreferencesManually,
    isLoading,
    loadUserPreferences
  };
};
